import {
	BASE_PRECISION_EXP,
	BigNum,
	PerpMarketConfig,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { COMMON_UI_UTILS, OpenPosition, UIMarket } from '@drift/common';
import { getMarginUsedForPosition } from 'src/utils/position';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { AccountOpenPosition } from 'src/@types/positions';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { OpenPositionsMobileCard } from './OpenPositionsMobileCard';
import { OpenPositionsDesktopRow } from './OpenPositionsDesktopRow';
import { BalanceDisplayType } from 'src/@types/types';
import { ExpandableItemParams } from 'src/hooks/lists/expandable/useExpandableItem';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

interface OpenPositionTableRowProps extends ExpandableItemParams {
	position: AccountOpenPosition;
	tableGrid: string;
	isMobile: boolean;
	handleSelection: (market: PerpMarketConfig) => void;
	handleShowPnlModal: (
		position: OpenPosition,
		pnl: number,
		isIsolated?: boolean
	) => void;
	sizeDisplayType: BalanceDisplayType;
	onToggleSizeDisplayType: () => void;
}

export const OpenPositionTableRow = ({
	position,
	tableGrid,
	isMobile,
	handleSelection,
	handleShowPnlModal,
	areAllItemsExpanded,
	handleItemExpandedChange,
	sizeDisplayType,
	onToggleSizeDisplayType,
}: OpenPositionTableRowProps) => {
	const { marketSymbol, marketIndex, direction, baseSize } = position;

	const setDrawers = useDrawersStore((s) => s.set);

	const baseSizeBigNum = BigNum.from(baseSize.abs(), BASE_PRECISION_EXP);

	const uiMarket = UIMarket.createPerpMarket(marketIndex);

	const showAccountValues = useShowAccountValues();
	const [pnlReference] = useSyncLocalStorage<'mark' | 'oracle'>(
		'pnlReference',
		'mark'
	);

	const positionPnl = BigNum.from(
		pnlReference === 'mark' ? position.pnlVsMark : position.pnlVsOracle,
		QUOTE_PRECISION_EXP
	);

	const netPnl = positionPnl; // we will remove funding pnl from the UI position table pnl for now, since the UI's wording needs to improve to better inform users that pnl includes funding pnl
	const pnl = netPnl.toNum();
	const notionalPnl = showAccountValues ? `${netPnl.toNotional()}` : '∗∗∗∗∗∗';

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const user = driftClient?.hasUser() ? driftClient?.getUser() : undefined;

	const percentageNum = COMMON_UI_UTILS.calculatePnlPctFromPosition(
		netPnl.val,
		position,
		user
			? getMarginUsedForPosition(user, position.marketIndex, false)
			: undefined
	);

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const handleShowPositionDrawer = () => {
		setDrawers((s) => {
			s.position.show = true;
			s.position.marketIndex = position.marketIndex;
		});
	};

	const handleShowPnlCard = () => {
		handleShowPnlModal(position, pnl, position.isIsolated);
	};

	return (
		<>
			{isMobile ? (
				<OpenPositionsMobileCard
					uiMarket={uiMarket}
					marketSymbol={marketSymbol}
					position={position}
					positionDirection={direction}
					showAccountValues={showAccountValues}
					displayValues={{
						baseSize: baseSizeBigNum,
						positionPnl: positionPnl,
						notionalPnl: notionalPnl,
						percentagePnl: percentagePnl,
					}}
					handleShowPnlCard={handleShowPnlCard}
					areAllItemsExpanded={areAllItemsExpanded}
					handleItemExpandedChange={handleItemExpandedChange}
					handleSelection={handleSelection}
					sizeDisplayType={sizeDisplayType}
					onToggleSizeDisplayType={onToggleSizeDisplayType}
				/>
			) : (
				<OpenPositionsDesktopRow
					position={position}
					tableGrid={tableGrid}
					handleSelection={handleSelection}
					handleShowPnlCard={handleShowPnlCard}
					handleShowPositionDrawer={handleShowPositionDrawer}
				/>
			)}
		</>
	);
};
